import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {useFormikContext} from 'formik';
import {useSearchParams} from 'react-router-dom';
import type {Field as FieldType} from 'components/customer/Product/entity/Field';
import type {Fieldset} from 'components/customer/Product/entity/Fieldset';
import {DrawerFields} from 'components/customer/Product/entity/Drawer';
import {
    MIXED,
    sharedValue,
    toNumber,
} from 'components/customer/Product/Drawer/helpers';
import {
    runnerWrites,
    withInnerType,
} from 'components/customer/Product/Drawer/helpers/hardwareWrites';
import {useDrawerParams} from 'components/customer/Product/Drawer/helpers/useDrawerParams';
import {useDrawerDefaults} from 'components/customer/Product/Drawer/DrawerDefaultsContext';
import {useGetRunnerQuery} from 'components/customer/Product/store/drawerRunnerApi';
import {useRunnerRequestParams} from 'components/customer/Product/Drawer/helpers/useRunnerRequestParams';
import {useAvailableDrawerSystems} from 'components/customer/Product/Drawer/helpers/useAvailableDrawerSystems';
import {
    HardwareDefaultsRow,
    Option,
} from 'components/customer/Product/Specs/HardwareDefaultsRow';

// 1, 2 and 3
const listed = (numbers: number[]) =>
    numbers.length > 1
        ? `${numbers.slice(0, -1).join(', ')} and ${numbers[numbers.length - 1]}`
        : String(numbers[0] ?? '');

interface InnerDrawerHardwareDefaultsProps {
    field: FieldType;
    fieldset: Fieldset;
}

// Cabinet-wide type and runner for inner drawers. Each is applied through the
// same writes as changing one drawer, so a new type still moves every position
// by the difference in bottom clearance.
export const InnerDrawerHardwareDefaults = ({
    field,
    fieldset,
}: InnerDrawerHardwareDefaultsProps) => {
    const drawerParams = useDrawerParams(field, fieldset);
    const {values, setFieldValue, setTouched} =
        useFormikContext<DrawerFields>();
    const {defaults, remember, chooseType} = useDrawerDefaults();
    const availableSystems = useAvailableDrawerSystems(true);
    const [searchParams] = useSearchParams();
    const isEditingProduct = searchParams.has('cabinetId');

    const typeField = drawerParams.drawerTypeFieldName;
    const runnerField = drawerParams.drawerRunnerFieldName;
    const drawers = values.drawers ?? [];

    const current = useMemo(
        () => ({
            type: sharedValue(drawers, typeField),
            runner: sharedValue(drawers, runnerField),
        }),
        [drawers, typeField, runnerField]
    );

    // hold what they agree on, so a later disagreement still has a default
    useEffect(() => {
        const agreed: Record<string, string> = {};

        if (current.type !== '' && current.type !== MIXED) {
            agreed.type = current.type;
        }

        if (current.runner !== '' && current.runner !== MIXED) {
            agreed.runner = current.runner;
        }

        remember(agreed);
    }, [current, remember]);

    // runners only accumulate as the height grows, so what fits the smallest
    // drawer fits them all
    const {smallest, at} = useMemo(() => {
        const heights = drawers.map((drawer) =>
            toNumber(drawer.drawer_face_height)
        );

        if (!heights.length || !heights.every((height) => height > 0)) {
            return {smallest: 0, at: []};
        }

        const least = Math.min(...heights);

        return {
            smallest: least,
            at: heights.flatMap((height, index) =>
                height === least ? [index + 1] : []
            ),
        };
    }, [drawers]);

    const {params} = useRunnerRequestParams(
        0,
        true,
        false,
        typeField,
        drawerParams.maxRunnerDepth,
        'drawer_face_height',
        drawerParams.fingerPullFieldName
    );

    const smallestParams = useMemo(
        () => ({...params, faceHeight: smallest}),
        [params, smallest]
    );

    // currentData, not data: while a new type fetches, data still holds the
    // old type's runners
    const {currentData: available} = useGetRunnerQuery(smallestParams, {
        skip: smallestParams.insertType == null || !(smallest > 0),
    });

    // every runner the type has, as the cabinet's height takes them all;
    // asked for once the list is first opened, as another request at mount
    // makes the drawers' own loading races likelier
    const [listing, setListing] = useState(false);
    const listHandler = useCallback(() => setListing(true), []);
    const cabinetHeight = toNumber(values.cabinet_height);
    const everyParams = useMemo(
        () => ({...params, faceHeight: cabinetHeight}),
        [params, cabinetHeight]
    );
    const {currentData: every} = useGetRunnerQuery(everyParams, {
        skip:
            !listing || everyParams.insertType == null || !(cabinetHeight > 0),
    });

    // a runner list is one type's, so there is none to offer across types
    const typeMixed = current.type === MIXED;

    // only when they arrive already disagreeing; the effect above holds
    // whatever they agree on
    useEffect(() => {
        if (defaults.runner !== '' || current.runner !== MIXED) return;

        const first = available?.[0];

        if (first) remember({runner: first.name});
    }, [defaults.runner, current.runner, available, remember]);

    // the runners that miss the smallest drawer stay listed, disabled, so
    // the list doesn't change under the customer as the drawers move
    const runnerOptions = useMemo<Option[]>(() => {
        const fits = new Set((available ?? []).map((runner) => runner.name));
        const space = `${Math.round(smallest * 10) / 10}mm`;
        const reason =
            at.length === 1
                ? `Too tall for drawer ${at[0]}'s ${space} space.`
                : `Too tall for drawers ${listed(at)} (${space} each).`;

        // nothing is known to fit until the smallest drawer's list arrives
        return (every ?? available ?? []).map((runner) => {
            const fitsAll = fits.has(runner.name);

            return {
                label: runner.name,
                value: runner.name,
                disabled: !fitsAll,
                reason: fitsAll || !available ? undefined : reason,
            };
        });
    }, [every, available, smallest, at]);

    const typeOptions = useMemo<Option[]>(
        () =>
            availableSystems.map((system) => ({
                label: system.name,
                value: String(system.id),
                disabled: system.disabled,
            })),
        [availableSystems]
    );

    const typeHandler = useCallback(
        (option: Option) => {
            if (!option || option.value === MIXED) return;

            const value = Number(option.value);
            const selected = availableSystems.find(
                (system) => system.id == value
            );

            chooseType(option.value);
            void setTouched({drawers: true});

            drawers.forEach((drawer, index) => {
                const currentSystem = availableSystems.find(
                    (system) =>
                        system.id == toNumber(drawer?.[String(typeField)])
                );

                if (currentSystem && selected) {
                    void setFieldValue(
                        `drawers[${index}]`,
                        withInnerType(drawer, currentSystem, selected)
                    );
                }

                void setFieldValue(`drawers[${index}].${typeField}`, value);
            });
        },
        [
            availableSystems,
            chooseType,
            drawers,
            setFieldValue,
            setTouched,
            typeField,
        ]
    );

    const runnerHandler = useCallback(
        (option: Option) => {
            if (!option || option.value === MIXED || typeMixed) return;

            const runner = available?.find(
                (item) => item.name === option.value
            );

            if (!runner) return;

            remember({runner: runner.name});
            void setTouched({drawers: true});

            drawers.forEach((drawer, index) => {
                runnerWrites(
                    index,
                    drawer,
                    runnerField,
                    runner.id,
                    runner,
                    isEditingProduct
                ).forEach(([path, value]) => {
                    void setFieldValue(path, value);
                });
            });
        },
        [
            available,
            drawers,
            isEditingProduct,
            remember,
            runnerField,
            setFieldValue,
            setTouched,
            typeMixed,
        ]
    );

    if (drawers.length === 0) return null;

    return (
        <HardwareDefaultsRow
            typeOptions={typeOptions}
            type={current.type}
            onTypeChange={typeHandler}
            runnerOptions={runnerOptions}
            runner={current.runner}
            onRunnerChange={runnerHandler}
            onRunnerMenuOpen={listHandler}
            runnerDisabled={typeMixed}
            runnerNote={
                typeMixed
                    ? 'Choose one type for every drawer first.'
                    : undefined
            }
            divided={false}
        />
    );
};
